import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDemoStaffQueue } from '../hooks/useDemoStaffQueue';

const COLOR = {
  bg: '#0F172A',
  bgAlt: '#1E293B',
  bgCard: '#1E293B',
  primary: '#7C3AED',
  primaryDark: '#6D28D9',
  primaryLight: 'rgba(124,58,237,0.12)',
  accent: '#06B6D4',
  accentGlow: 'rgba(6,182,212,0.15)',
  text: '#F8FAFC',
  textSecondary: '#CBD5E1',
  textMuted: '#94A3B8',
  border: '#334155',
  borderHover: '#475569',
  white: '#FFFFFF',
  green: '#10B981',
  gradientStart: '#7C3AED',
  gradientEnd: '#06B6D4',
};

/* ------------------------------------------------------------------ */
/*  Navbar                                                             */
/* ------------------------------------------------------------------ */

function Navbar({ t }) {
  const [open, setOpen] = useState(false);

  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, background: `linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>vQueue</span>
        </Link>

        <button className="lp-hamburger" onClick={() => setOpen(!open)} aria-label={t('landing.nav.menu')}>
          <span /><span /><span />
        </button>

        <div className={`lp-nav-links ${open ? 'open' : ''}`}>
          <a href="#features" onClick={() => setOpen(false)}>{t('landing.nav.features')}</a>
          <a href="#how" onClick={() => setOpen(false)}>{t('landing.how.title')}</a>
          <a href="#demo" onClick={() => setOpen(false)}>{t('landing.demo.navLabel')}</a>
          <a href="#pricing" onClick={() => setOpen(false)}>{t('landing.nav.pricing')}</a>
          <a href="#faq" onClick={() => setOpen(false)}>{t('landing.nav.faq')}</a>
          <a href="#contact" onClick={() => setOpen(false)}>{t('landing.nav.contact')}</a>
          <Link to="/admin/login" className="lp-nav-signin" onClick={() => setOpen(false)}>{t('landing.nav.signIn')}</Link>
          <Link to="/admin/signup" className="lp-btn-primary lp-btn-sm" onClick={() => setOpen(false)}>{t('landing.nav.getStarted')}</Link>
        </div>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */

function Hero({ t }) {
  return (
    <section className="lp-hero">
      <div className="lp-hero-glow" />
      <div className="lp-container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="lp-hero-preheadline">{t('landing.hero.preHeadline')}</div>
        <h1 className="lp-hero-h1">{t('landing.hero.headline')}</h1>
        <p className="lp-hero-sub">{t('landing.hero.subheadline')}</p>
        <div className="lp-hero-ctas">
          <Link to="/admin/signup" className="lp-btn-primary lp-btn-lg">{t('landing.hero.cta')}</Link>
          <a href="#demo" className="lp-btn-secondary lp-btn-lg">{t('landing.hero.ctaSecondary')}</a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Built For (target verticals)                                       */
/* ------------------------------------------------------------------ */

const FOR_EMOJI = ['\u{1F3E5}', '\u{1F527}', '\u{1F3DB}', '\u{1F4F1}'];
const FOR_KEYS = ['clinic', 'carService', 'government', 'repair'];

function BuiltFor({ t }) {
  return (
    <section className="lp-section lp-for-section">
      <div className="lp-container">
        <h2 className="lp-section-title">{t('landing.for.title')}</h2>
        <p className="lp-section-sub">{t('landing.for.subtitle')}</p>
        <div className="lp-for-grid">
          {FOR_KEYS.map((key, i) => (
            <div key={key} className="lp-for-card">
              <div className="lp-for-icon">{FOR_EMOJI[i]}</div>
              <h3 className="lp-for-title">{t(`landing.for.${key}.title`)}</h3>
              <p className="lp-for-desc">{t(`landing.for.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Value Props                                                        */
/* ------------------------------------------------------------------ */

// Order matters — paired by index with VALUE_KEYS below.
const VALUE_EMOJI = ['\u{1F3AB}', '\u{1F4F1}', '\u{1F4FA}', '\u{1F6E1}\uFE0F', '\u{1F504}', '\u{1F4CA}'];
const VALUE_KEYS  = ['walkUp',   'realtime', 'display',  'privacy',             'dynamicQr', 'insights'];

function ValueProps({ t }) {
  return (
    <section id="features" className="lp-section" style={{ background: COLOR.bgAlt, borderTop: `1px solid ${COLOR.border}`, borderBottom: `1px solid ${COLOR.border}` }}>
      <div className="lp-container">
        <h2 className="lp-section-title">{t('landing.value.title')}</h2>
        <div className="lp-grid-3">
          {VALUE_KEYS.map((key, i) => (
            <div key={key} className="lp-card">
              <div className="lp-card-icon">{VALUE_EMOJI[i]}</div>
              <h3 className="lp-card-title">{t(`landing.value.${key}.title`)}</h3>
              <p className="lp-card-desc">{t(`landing.value.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Full Feature List                                                  */
/* ------------------------------------------------------------------ */

const FEATURE_KEYS = [
  'alerts', 'waitTracking', 'customerList', 'realtime', 'kiosk',
  'display', 'dynamicQr', 'analytics', 'multiLocation', 'languages',
];

function FeatureList({ t }) {
  return (
    <section id="feature-list" className="lp-section">
      <div className="lp-container" style={{ maxWidth: 960 }}>
        <h2 className="lp-section-title">{t('landing.featureList.title')}</h2>
        <p className="lp-section-sub">{t('landing.featureList.subtitle')}</p>
        <div className="lp-featurelist-grid">
          {FEATURE_KEYS.map((key) => (
            <div key={key} className="lp-featurelist-item">
              <span className="lp-featurelist-check">{'✓'}</span>
              <div>
                <h3 className="lp-featurelist-title">{t(`landing.featureList.${key}.title`)}</h3>
                <p className="lp-featurelist-desc">{t(`landing.featureList.${key}.desc`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  How It Works                                                       */
/* ------------------------------------------------------------------ */

const STEP_EMOJI = ['\uD83D\uDCF7', '\uD83D\uDC4B', '\u2705', '\u{1F4FA}'];

function HowItWorks({ t }) {
  return (
    <section id="how" className="lp-section">
      <div className="lp-container">
        <h2 className="lp-section-title">{t('landing.how.title')}</h2>
        <div className="lp-grid-4">
          {[1, 2, 3, 4].map((n, i) => (
            <div key={n} className="lp-step">
              <div className="lp-step-num">{n}</div>
              <div className="lp-step-icon">{STEP_EMOJI[i]}</div>
              <h3 className="lp-card-title">{t(`landing.how.step${n}.title`)}</h3>
              <p className="lp-card-desc">{t(`landing.how.step${n}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Live Demo                                                          */
/* ------------------------------------------------------------------ */

const DEMO_SHOP_ID = '00000000-0000-0000-0000-000000000002';

const DEMO_AVATARS = {
  smile: '\u{1F600}', cool: '\u{1F60E}', star: '\u{1F929}', party: '\u{1F973}',
  cat: '\u{1F63A}', fox: '\u{1F98A}', penguin: '\u{1F427}', sparkle: '\u{1F31F}',
  robot: '\u{1F916}', alien: '\u{1F47D}', unicorn: '\u{1F984}', bear: '\u{1F43B}',
  dog: '\u{1F436}', octopus: '\u{1F419}', butterfly: '\u{1F98B}', dragon: '\u{1F409}',
  astronaut: '\u{1F680}', wizard: '\u{1F9D9}',
};

function LiveDemo({ t }) {
  const demoUrl = `${window.location.origin}/q/${DEMO_SHOP_ID}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(demoUrl)}`;
  const { entries, wsStatus, callNext, finishTicket } = useDemoStaffQueue(DEMO_SHOP_ID);
  const [calledUuid, setCalledUuid] = useState(null);

  // Clear called state when entry disappears from queue
  useEffect(() => {
    if (calledUuid && !entries.find(e => e.ticketUuid === calledUuid)) {
      setCalledUuid(null);
    }
  }, [entries, calledUuid]);

  function handleCallNext() {
    callNext();
    // The called ticket is popped from the queue, we track it via the next update
    if (entries.length > 0) setCalledUuid(entries[0].ticketUuid);
  }

  return (
    <section id="demo" className="lp-section" style={{ background: COLOR.bgAlt, borderTop: `1px solid ${COLOR.border}`, borderBottom: `1px solid ${COLOR.border}` }}>
      <div className="lp-container">
        <h2 className="lp-section-title">{t('landing.demo.title')}</h2>
        <p className="lp-section-sub">
          {t('landing.demo.subtitle')}
        </p>
        <div className="lp-demo-panels">
          {/* B2C panel — customer side */}
          <div className="lp-demo-panel">
            <div className="lp-demo-panel-header">
              <span className="lp-demo-badge lp-demo-badge-blue">{t('landing.demo.customerLabel')}</span>
            </div>
            <div className="lp-demo-qr">
              <img src={qrSrc} alt={t('landing.demo.qrAlt')} width={180} height={180} style={{ borderRadius: 12 }} />
            </div>
            <p className="lp-card-desc" style={{ textAlign: 'center', margin: '12px 0 16px' }}>
              {t('landing.demo.scanHint')}
            </p>
            <a href={demoUrl} target="_blank" rel="noopener noreferrer" className="lp-btn-primary lp-btn-md lp-btn-full">
              {t('landing.demo.cta')}
            </a>
          </div>

          {/* B2B panel — staff side */}
          <div className="lp-demo-panel lp-demo-panel-staff">
            <div className="lp-demo-panel-header">
              <span className="lp-demo-badge lp-demo-badge-indigo">{t('landing.demo.staffLabel')}</span>
              <span className="lp-demo-status" style={{ color: wsStatus === 'connected' ? COLOR.green : COLOR.textMuted }}>
                {wsStatus === 'connected' ? `\u25CF ${t('landing.demo.live')}` : `\u25CB ${t('landing.demo.connecting')}`}
              </span>
            </div>

            {entries.length === 0 ? (
              <div className="lp-demo-empty">
                <span style={{ fontSize: '2rem' }}>{'\u{1F389}'}</span>
                <p>{t('landing.demo.emptyQueue')}</p>
              </div>
            ) : (
              <div className="lp-demo-queue-list">
                {entries.map((entry) => (
                  <div key={entry.ticketUuid} className="lp-demo-entry">
                    <span className="lp-demo-entry-pos">#{entry.ticketNumber || entry.position}</span>
                    <span className="lp-demo-entry-avatar">{DEMO_AVATARS[entry.avatarId] || '\u{1F600}'}</span>
                    <span className="lp-demo-entry-name">{entry.firstName || t('avatars.guest')}</span>
                    <button
                      className="lp-demo-btn-done"
                      onClick={() => finishTicket(entry.ticketUuid)}
                    >
                      {t('landing.demo.done')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              className="lp-btn-primary lp-btn-md lp-btn-full"
              onClick={handleCallNext}
              disabled={entries.length === 0}
              style={entries.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {t('landing.demo.callNext')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Pricing                                                            */
/* ------------------------------------------------------------------ */

const TIERS = ['solo', 'highStreet', 'chain'];

function Pricing({ t }) {
  return (
    <section id="pricing" className="lp-section" style={{ background: COLOR.bgAlt, borderTop: `1px solid ${COLOR.border}`, borderBottom: `1px solid ${COLOR.border}` }}>
      <div className="lp-container">
        <h2 className="lp-section-title">{t('landing.pricing.title')}</h2>
        <p className="lp-section-sub">{t('landing.pricing.subtitle')}</p>
        <div className="lp-grid-3 lp-pricing-grid">
          {TIERS.map((tier) => {
            const badge = t(`landing.pricing.${tier}.badge`, { defaultValue: '' });
            const isPopular = tier === 'highStreet';
            return (
              <div key={tier} className={`lp-price-card ${isPopular ? 'lp-price-popular' : ''}`}>
                {badge && <span className="lp-price-badge">{badge}</span>}
                <h3 className="lp-price-name">{t(`landing.pricing.${tier}.name`)}</h3>
                <p className="lp-price-anchor">{t(`landing.pricing.${tier}.anchor`)}</p>
                <div className="lp-price-amount">
                  <span className="lp-price-number">{t(`landing.pricing.${tier}.price`)}</span>
                  <span className="lp-price-period">{t(`landing.pricing.${tier}.period`)}</span>
                </div>
                <ul className="lp-price-features">
                  {(t(`landing.pricing.${tier}.features`, { returnObjects: true }) || []).map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <Link
                  to="/admin/signup"
                  className={isPopular ? 'lp-btn-primary lp-btn-md lp-btn-full' : 'lp-btn-secondary lp-btn-md lp-btn-full'}
                >
                  {t(`landing.pricing.${tier}.cta`)}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  ROI Calculator — self-qualify before the FAQ                       */
/* ------------------------------------------------------------------ */

const ROI_RECOVERY_RATE = 0.6;

function formatCurrency(amount) {
  return `€${Math.round(amount).toLocaleString('en-US')}`;
}

function ROICalculator({ t }) {
  const [walkoffs, setWalkoffs] = useState(5);
  const [value, setValue] = useState(20);
  const [days, setDays] = useState(22);
  const recovered = walkoffs * ROI_RECOVERY_RATE * value * days;

  return (
    <section id="roi" className="lp-section lp-roi">
      <div className="lp-container" style={{ maxWidth: 860 }}>
        <h2 className="lp-section-title">{t('landing.roi.title')}</h2>
        <p className="lp-section-sub">{t('landing.roi.subtitle')}</p>
        <div className="lp-roi-card">
          <div className="lp-roi-sliders">
            <div className="lp-roi-slider">
              <label className="lp-roi-label">
                {t('landing.roi.walkoffsLabel')}
                <span className="lp-roi-value">{walkoffs}</span>
              </label>
              <input
                type="range"
                min={0}
                max={20}
                value={walkoffs}
                onChange={(e) => setWalkoffs(Number(e.target.value))}
                className="lp-roi-range"
              />
            </div>
            <div className="lp-roi-slider">
              <label className="lp-roi-label">
                {t('landing.roi.valueLabel')}
                <span className="lp-roi-value">{formatCurrency(value)}</span>
              </label>
              <input
                type="range"
                min={5}
                max={200}
                step={5}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                className="lp-roi-range"
              />
            </div>
            <div className="lp-roi-slider">
              <label className="lp-roi-label">
                {t('landing.roi.daysLabel')}
                <span className="lp-roi-value">{days}</span>
              </label>
              <input
                type="range"
                min={10}
                max={31}
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="lp-roi-range"
              />
            </div>
          </div>
          <div className="lp-roi-result">
            <div className="lp-roi-result-label">{t('landing.roi.recoveredLabel')}</div>
            <div className="lp-roi-result-value">{formatCurrency(recovered)}</div>
            <Link to="/admin/signup" className="lp-btn-primary lp-btn-md lp-btn-full" style={{ marginTop: 16 }}>
              {t('landing.roi.cta')}
            </Link>
          </div>
        </div>
        <p className="lp-roi-footnote">{t('landing.roi.footnote')}</p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */

function FAQ({ t }) {
  const [openIdx, setOpenIdx] = useState(null);
  const items = t('landing.faq.items', { returnObjects: true }) || [];

  return (
    <section id="faq" className="lp-section">
      <div className="lp-container" style={{ maxWidth: 720 }}>
        <h2 className="lp-section-title">{t('landing.faq.title')}</h2>
        <div className="lp-faq-list">
          {items.map((item, i) => (
            <div key={i} className="lp-faq-item">
              <button className="lp-faq-q" onClick={() => setOpenIdx(openIdx === i ? null : i)}>
                <span>{item.q}</span>
                <span className="lp-faq-arrow">{openIdx === i ? '\u2212' : '+'}</span>
              </button>
              {openIdx === i && <div className="lp-faq-a">{item.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Contact                                                            */
/* ------------------------------------------------------------------ */

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

function Contact({ t }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null); // 'sending' | 'sent' | 'error'

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    setErrorMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), message: message.trim() }),
      });
      if (res.ok || res.status === 202) {
        setStatus('sent');
        setEmail('');
        setMessage('');
        return;
      }
      // Surface the backend error so rate-limit / validation failures don't
      // hide behind a generic "Something went wrong".
      let body = null;
      try { body = await res.json(); } catch { /* non-JSON body */ }
      if (res.status === 429) {
        setErrorMessage(t('landing.contact.rateLimited'));
      } else if (body?.message) {
        setErrorMessage(body.message);
      } else {
        setErrorMessage(t('landing.contact.error'));
      }
      setStatus('error');
    } catch {
      setErrorMessage(t('landing.contact.error'));
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="lp-section" style={{ background: COLOR.bgAlt, borderTop: `1px solid ${COLOR.border}` }}>
      <div className="lp-container" style={{ maxWidth: 600 }}>
        <h2 className="lp-section-title">{t('landing.contact.title')}</h2>
        <p className="lp-section-sub">{t('landing.contact.subtitle')}</p>

        {status === 'sent' ? (
          <div className="lp-contact-success">
            <p>{t('landing.contact.sent')}</p>
          </div>
        ) : (
          <form className="lp-contact-form" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder={t('landing.contact.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              required
              className="lp-contact-input"
            />
            <textarea
              placeholder={t('landing.contact.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              required
              rows={5}
              className="lp-contact-textarea"
            />
            {status === 'error' && (
              <p style={{ color: '#EF4444', fontSize: '0.9rem', margin: '0 0 0.75rem' }}>{errorMessage || t('landing.contact.error')}</p>
            )}
            <button
              type="submit"
              className="lp-btn-primary lp-btn-lg lp-contact-btn"
              disabled={status === 'sending' || !message.trim()}
            >
              {status === 'sending' ? t('landing.contact.sending') : t('landing.contact.cta')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Footer                                                             */
/* ------------------------------------------------------------------ */

function Footer({ t }) {
  const year = new Date().getFullYear();
  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer-inner">
          <div>
            <span style={{ fontWeight: 800, fontSize: '1.25rem', background: `linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>vQueue</span>
            <p style={{ color: COLOR.textMuted, fontSize: '0.85rem', marginTop: 8, maxWidth: 300 }}>
              {t('landing.footer.tagline')}
            </p>
          </div>
          <div className="lp-footer-links">
            <Link to="/admin/signup">{t('landing.footer.signUp')}</Link>
            <Link to="/admin/login">{t('landing.footer.signIn')}</Link>
            <Link to="/privacy">{t('landing.footer.privacy')}</Link>
            <Link to="/terms">{t('landing.footer.terms')}</Link>
            <Link to="/imprint">{t('landing.footer.imprint')}</Link>
            <Link to="/legal/dpa">{t('landing.footer.dpa')}</Link>
            <a href="#contact">{t('landing.footer.contact')}</a>
          </div>
        </div>
        <div className="lp-footer-copy">
          &copy; {year} {t('landing.footer.copyright')}
        </div>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/*  Embedded CSS (responsive + pseudo-classes)                         */
/* ------------------------------------------------------------------ */

const CSS = `
/* ---- base ---- */
.lp-container { max-width: 1120px; margin: 0 auto; padding: 0 1.25rem; }
.lp-section { padding: 5rem 0; }
.lp-section-title { font-size: 2rem; font-weight: 800; color: ${COLOR.text}; text-align: center; margin-bottom: 0.75rem; letter-spacing: -0.02em; }
.lp-section-sub { text-align: center; color: ${COLOR.textSecondary}; font-size: 1.05rem; margin-bottom: 3rem; }

/* ---- nav ---- */
.lp-nav { position: sticky; top: 0; z-index: 100; background: rgba(15,23,42,0.9); backdrop-filter: blur(16px) saturate(1.4); border-bottom: 1px solid ${COLOR.border}; }
.lp-nav-inner { max-width: 1120px; margin: 0 auto; padding: 0 1.25rem; height: 64px; display: flex; align-items: center; justify-content: space-between; }
.lp-nav-links { display: flex; align-items: center; gap: 1.5rem; }
.lp-nav-links a { text-decoration: none; color: ${COLOR.textMuted}; font-size: 0.9rem; font-weight: 500; transition: color 0.2s; }
.lp-nav-links a:hover { color: ${COLOR.text}; }
.lp-nav-signin { color: ${COLOR.accent} !important; font-weight: 600 !important; }
.lp-hamburger { display: none; background: none; border: none; cursor: pointer; padding: 4px; flex-direction: column; gap: 5px; }
.lp-hamburger span { display: block; width: 22px; height: 2px; background: ${COLOR.textSecondary}; border-radius: 2px; }

/* ---- buttons ---- */
.lp-btn-primary { display: inline-block; background: linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd}); color: #fff; text-decoration: none; border-radius: 10px; font-weight: 700; text-align: center; transition: box-shadow 0.25s, transform 0.15s; border: none; cursor: pointer; }
.lp-btn-primary:hover { box-shadow: 0 0 28px rgba(124,58,237,0.25), 0 4px 20px rgba(6,182,212,0.2); transform: translateY(-1px); }
.lp-btn-secondary { display: inline-block; background: ${COLOR.bgAlt}; color: ${COLOR.textSecondary}; text-decoration: none; border-radius: 10px; font-weight: 600; text-align: center; border: 1px solid ${COLOR.border}; transition: border-color 0.2s, color 0.2s; cursor: pointer; }
.lp-btn-secondary:hover { border-color: ${COLOR.accent}; color: ${COLOR.text}; }
.lp-btn-sm { padding: 0.45rem 1rem; font-size: 0.85rem; }
.lp-btn-md { padding: 0.6rem 1.5rem; font-size: 0.95rem; }
.lp-btn-lg { padding: 0.85rem 2rem; font-size: 1.05rem; }
.lp-btn-full { display: block; width: 100%; }

/* ---- hero ---- */
.lp-hero { position: relative; padding: 7rem 0 6rem; text-align: center; background: ${COLOR.bg}; overflow: hidden; }
.lp-hero-glow { position: absolute; top: -50%; left: 50%; transform: translateX(-50%); width: 900px; height: 900px; border-radius: 50%; background: radial-gradient(circle, rgba(124,58,237,0.12) 0%, rgba(6,182,212,0.06) 40%, transparent 70%); pointer-events: none; }
.lp-hero-badge { display: inline-block; padding: 6px 18px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; color: ${COLOR.accent}; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.25); margin-bottom: 1.5rem; letter-spacing: 0.02em; }
.lp-hero-preheadline { font-size: 0.95rem; font-weight: 600; color: ${COLOR.accent}; max-width: 620px; margin: 0 auto 1.25rem; letter-spacing: 0.01em; }
.lp-hero-h1 { font-size: 3.25rem; font-weight: 800; color: ${COLOR.text}; line-height: 1.12; max-width: 720px; margin: 0 auto 1.25rem; letter-spacing: -0.03em; }
.lp-hero-sub { font-size: 1.15rem; color: ${COLOR.textSecondary}; max-width: 560px; margin: 0 auto 2.5rem; line-height: 1.6; }
.lp-hero-ctas { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

/* ---- grid ---- */
.lp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
.lp-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.25rem; }

/* ---- ROI calculator ---- */
.lp-roi { background: ${COLOR.bg}; }
.lp-roi-card {
  display: grid; grid-template-columns: 1.4fr 1fr; gap: 2rem;
  background: ${COLOR.bgCard}; border-radius: 16px; padding: 2rem;
  border: 1px solid ${COLOR.border};
}
.lp-roi-sliders { display: flex; flex-direction: column; gap: 1.5rem; }
.lp-roi-label {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 0.9rem; font-weight: 600; color: ${COLOR.textSecondary}; margin-bottom: 0.5rem;
}
.lp-roi-value { color: ${COLOR.accent}; font-weight: 700; }
.lp-roi-range { width: 100%; accent-color: ${COLOR.primary}; }
.lp-roi-result {
  display: flex; flex-direction: column; justify-content: center; text-align: center;
  background: ${COLOR.bg}; border-radius: 12px; padding: 1.5rem;
  border: 1px solid ${COLOR.border};
}
.lp-roi-result-label {
  font-size: 0.85rem; color: ${COLOR.textMuted};
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem;
}
.lp-roi-result-value {
  font-size: 2.5rem; font-weight: 800; letter-spacing: -0.03em;
  background: linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd});
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; line-height: 1;
}
.lp-roi-footnote {
  text-align: center; font-size: 0.78rem; color: ${COLOR.textMuted};
  margin: 1.25rem auto 0; max-width: 640px; font-style: italic;
}

/* ---- price tier anchor subtitle ---- */
.lp-price-anchor {
  font-size: 0.85rem; color: ${COLOR.textMuted}; margin: 0 0 1.25rem; min-height: 1.25rem;
}

/* ---- built-for verticals ---- */
.lp-for-section { background: ${COLOR.bg}; border-bottom: 1px solid ${COLOR.border}; }
.lp-for-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem;
}
.lp-for-card {
  background: ${COLOR.bgAlt}; border-radius: 12px; padding: 1.5rem 1.25rem;
  border: 1px solid ${COLOR.border}; transition: border-color 0.25s, transform 0.2s;
}
.lp-for-card:hover { border-color: ${COLOR.accent}; transform: translateY(-2px); }
.lp-for-icon { font-size: 1.75rem; margin-bottom: 0.75rem; }
.lp-for-title {
  font-size: 0.98rem; font-weight: 700; color: ${COLOR.text};
  margin: 0 0 0.4rem; line-height: 1.25;
}
.lp-for-desc {
  font-size: 0.85rem; color: ${COLOR.textSecondary}; line-height: 1.55; margin: 0;
}

/* ---- cards ---- */
.lp-card { background: ${COLOR.bgCard}; border-radius: 14px; padding: 2rem 1.5rem; border: 1px solid ${COLOR.border}; transition: border-color 0.25s, transform 0.2s, box-shadow 0.25s; }
.lp-card:hover { border-color: ${COLOR.borderHover}; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
.lp-card-icon { font-size: 2rem; margin-bottom: 1rem; }
.lp-card-title { font-size: 1.1rem; font-weight: 700; color: ${COLOR.text}; margin-bottom: 0.5rem; }
.lp-card-desc { font-size: 0.9rem; color: ${COLOR.textSecondary}; line-height: 1.6; }

/* ---- full feature list ---- */
.lp-featurelist-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem 2.5rem; }
.lp-featurelist-item { display: flex; gap: 12px; align-items: flex-start; }
.lp-featurelist-check { color: ${COLOR.green}; font-weight: 700; font-size: 1rem; line-height: 1.5; flex-shrink: 0; }
.lp-featurelist-title { font-size: 0.98rem; font-weight: 700; color: ${COLOR.text}; margin: 0 0 0.25rem; }
.lp-featurelist-desc { font-size: 0.85rem; color: ${COLOR.textSecondary}; line-height: 1.55; margin: 0; }

/* ---- steps ---- */
.lp-step { text-align: center; padding: 1.5rem; }
.lp-step-num { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd}); color: #fff; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.1rem; margin-bottom: 1rem; box-shadow: 0 4px 16px rgba(124,58,237,0.3); }
.lp-step-icon { font-size: 2.5rem; margin-bottom: 1rem; }

/* ---- pricing ---- */
.lp-pricing-grid { align-items: start; }
.lp-price-card { background: ${COLOR.bgCard}; border-radius: 14px; padding: 2rem 1.5rem; border: 1px solid ${COLOR.border}; position: relative; display: flex; flex-direction: column; transition: border-color 0.25s, transform 0.2s; }
.lp-price-card:hover { border-color: ${COLOR.borderHover}; transform: translateY(-2px); }
.lp-price-popular { border-color: ${COLOR.primary}; box-shadow: 0 0 40px rgba(124,58,237,0.15), 0 8px 32px rgba(0,0,0,0.25); }
.lp-price-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, ${COLOR.gradientStart}, ${COLOR.gradientEnd}); color: #fff; padding: 4px 16px; border-radius: 20px; font-size: 0.75rem; font-weight: 700; white-space: nowrap; }
.lp-price-name { font-size: 1.25rem; font-weight: 700; color: ${COLOR.text}; margin-bottom: 0.5rem; }
.lp-price-amount { margin-bottom: 1.5rem; }
.lp-price-number { font-size: 2.5rem; font-weight: 800; color: ${COLOR.text}; }
.lp-price-period { font-size: 1rem; color: ${COLOR.textMuted}; }
.lp-price-features { list-style: none; margin-bottom: 2rem; flex: 1; }
.lp-price-features li { padding: 0.4rem 0; font-size: 0.9rem; color: ${COLOR.textSecondary}; }
.lp-price-features li::before { content: '\\2713'; color: ${COLOR.green}; font-weight: 700; margin-right: 8px; }

/* ---- faq ---- */
.lp-faq-list { display: flex; flex-direction: column; }
.lp-faq-item { border-bottom: 1px solid ${COLOR.border}; }
.lp-faq-q { width: 100%; background: none; border: none; cursor: pointer; display: flex; justify-content: space-between; align-items: center; padding: 1.25rem 0; font-size: 1rem; font-weight: 600; color: ${COLOR.text}; text-align: left; font-family: inherit; transition: color 0.2s; }
.lp-faq-q:hover { color: ${COLOR.accent}; }
.lp-faq-arrow { font-size: 1.25rem; color: ${COLOR.textMuted}; flex-shrink: 0; margin-left: 1rem; }
.lp-faq-a { padding: 0 0 1.25rem; font-size: 0.9rem; color: ${COLOR.textSecondary}; line-height: 1.7; }

/* ---- demo ---- */
.lp-demo-panels { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; max-width: 820px; margin: 0 auto; }
.lp-demo-panel { background: ${COLOR.bgCard}; border-radius: 16px; padding: 1.5rem; border: 1px solid ${COLOR.border}; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.lp-demo-panel-staff { align-items: stretch; }
.lp-demo-panel-header { display: flex; justify-content: space-between; align-items: center; width: 100%; margin-bottom: 4px; }
.lp-demo-badge { font-size: 0.75rem; font-weight: 700; padding: 4px 12px; border-radius: 20px; letter-spacing: 0.03em; }
.lp-demo-badge-blue { background: rgba(6,182,212,0.12); color: ${COLOR.accent}; }
.lp-demo-badge-indigo { background: ${COLOR.primaryLight}; color: ${COLOR.primary}; }
.lp-demo-status { font-size: 0.8rem; font-weight: 600; }
.lp-demo-qr { flex-shrink: 0; padding: 0.75rem; border-radius: 12px; border: 1px solid ${COLOR.border}; background: #fff; }
.lp-demo-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 2rem 1rem; color: ${COLOR.textMuted}; font-size: 0.9rem; text-align: center; }
.lp-demo-queue-list { flex: 1; display: flex; flex-direction: column; gap: 8px; width: 100%; margin-bottom: 8px; max-height: 260px; overflow-y: auto; }
.lp-demo-entry { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: ${COLOR.bg}; border-radius: 10px; border: 1px solid ${COLOR.border}; }
.lp-demo-entry-pos { font-weight: 800; font-size: 0.9rem; color: ${COLOR.accent}; min-width: 32px; }
.lp-demo-entry-avatar { font-size: 1.3rem; }
.lp-demo-entry-name { flex: 1; font-weight: 600; font-size: 0.9rem; color: ${COLOR.text}; }
.lp-demo-btn-done { padding: 5px 12px; border-radius: 6px; border: none; background: ${COLOR.green}; color: #fff; font-weight: 600; font-size: 0.8rem; cursor: pointer; transition: opacity 0.15s; }
.lp-demo-btn-done:hover { opacity: 0.85; }

/* ---- contact ---- */
.lp-contact-form { display: flex; flex-direction: column; gap: 0.75rem; }
.lp-contact-input, .lp-contact-textarea {
  width: 100%; padding: 0.75rem 1rem; background: ${COLOR.bg}; color: ${COLOR.text};
  border: 1px solid ${COLOR.border}; border-radius: 10px; font-size: 0.95rem;
  font-family: inherit; transition: border-color 0.2s; outline: none;
}
.lp-contact-input:focus, .lp-contact-textarea:focus { border-color: ${COLOR.accent}; }
.lp-contact-textarea { resize: vertical; min-height: 120px; }
.lp-contact-btn { display: inline-block; padding: 0.85rem 2.5rem; font-size: 1.1rem; align-self: flex-start; }
.lp-contact-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.lp-contact-success {
  text-align: center; padding: 2rem; background: ${COLOR.bg}; border-radius: 14px;
  border: 1px solid ${COLOR.border}; color: ${COLOR.green}; font-weight: 600; font-size: 1.05rem;
}
@media (max-width: 768px) {
  .lp-contact-btn { align-self: stretch; width: 100%; }
}

/* ---- footer ---- */
.lp-footer { background: #0B1120; border-top: 1px solid ${COLOR.border}; padding: 3rem 0 1.5rem; }
.lp-footer-inner { display: flex; justify-content: space-between; align-items: flex-start; gap: 2rem; margin-bottom: 2rem; }
.lp-footer-links { display: flex; gap: 1.5rem; flex-wrap: wrap; }
.lp-footer-links a { color: ${COLOR.textMuted}; text-decoration: none; font-size: 0.85rem; transition: color 0.15s; }
.lp-footer-links a:hover { color: ${COLOR.text}; }
.lp-footer-copy { border-top: 1px solid ${COLOR.border}; padding-top: 1.5rem; font-size: 0.8rem; color: ${COLOR.textMuted}; text-align: center; }

/* ---- responsive ---- */
@media (max-width: 768px) {
  .lp-grid-3 { grid-template-columns: 1fr; }
  .lp-grid-4 { grid-template-columns: 1fr; gap: 1rem; }
  .lp-for-grid { grid-template-columns: 1fr; }
  .lp-featurelist-grid { grid-template-columns: 1fr; }
  .lp-hero-h1 { font-size: 2rem; }
  .lp-hero-sub { font-size: 1rem; }
  .lp-hero { padding: 4rem 0 3rem; }
  .lp-hero-badge { font-size: 0.72rem; }
  .lp-hero-preheadline { font-size: 0.85rem; }
  .lp-roi-card { grid-template-columns: 1fr; padding: 1.5rem; }
  .lp-section { padding: 3.5rem 0; }
  .lp-section-title { font-size: 1.5rem; }
  .lp-footer-inner { flex-direction: column; gap: 1.5rem; }
  .lp-demo-panels { grid-template-columns: 1fr; }
  .lp-nav-links { display: none; position: absolute; top: 64px; left: 0; right: 0; background: rgba(15,23,42,0.97); flex-direction: column; padding: 1rem 1.25rem; border-bottom: 1px solid ${COLOR.border}; gap: 1rem; box-shadow: 0 16px 40px rgba(0,0,0,0.3); backdrop-filter: blur(16px); }
  .lp-nav-links.open { display: flex; }
  .lp-hamburger { display: flex; }
}
@media (min-width: 769px) and (max-width: 1024px) {
  .lp-grid-3 { grid-template-columns: repeat(2, 1fr); }
  .lp-grid-4 { grid-template-columns: repeat(2, 1fr); }
  .lp-for-grid { grid-template-columns: repeat(2, 1fr); }
  .lp-hero-h1 { font-size: 2.5rem; }
}
`;

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function LandingPage() {
  const { t } = useTranslation();

  return (
    <>
      <style>{CSS}</style>
      <Navbar t={t} />
      <Hero t={t} />
      <BuiltFor t={t} />
      <LiveDemo t={t} />
      <HowItWorks t={t} />
      <ValueProps t={t} />
      <FeatureList t={t} />
      <Pricing t={t} />
      <ROICalculator t={t} />
      <FAQ t={t} />
      <Contact t={t} />
      <Footer t={t} />
    </>
  );
}
